import { useEffect, useState } from 'react';

export interface RotatingWallpaperState {
  urlA: string | null;
  urlB: string | null;
  showA: boolean;
}

const INITIAL_STATE: RotatingWallpaperState = {
  urlA: null,
  urlB: null,
  showA: true,
};

const shuffleInPlace = <T,>(arr: T[]): T[] => {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

export function useRotatingWallpaper(
  urls: readonly string[],
  intervalMs = 10_000
): RotatingWallpaperState {
  const [state, setState] = useState<RotatingWallpaperState>(INITIAL_STATE);

  useEffect(() => {
    if (urls.length === 0) return;

    const queue = shuffleInPlace([...urls]);
    let cancelled = false;

    const advance = (): void => {
      while (queue.length > 0 && !cancelled) {
        const url = queue.shift()!;
        const img = new Image();
        img.onload = () => {
          if (cancelled) return;
          setState((s) =>
            s.urlA === null
              ? { urlA: url, urlB: null, showA: true }
              : s.showA
                ? { ...s, urlB: url, showA: false }
                : { ...s, urlA: url, showA: true }
          );
        };
        img.onerror = advance;
        img.src = url;
        return;
      }
    };

    advance();
    const timerId = window.setInterval(advance, intervalMs);

    return () => {
      cancelled = true;
      clearInterval(timerId);
    };
  }, [urls, intervalMs]);

  return state;
}
